import { useState, useEffect } from "react";
import { useQuery } from "convex/react";
import { api as convexApi } from "../lib/convexApi";
import { navigateRoute } from "../routeNav";
import { useEntitlements } from "../hooks/useEntitlements";
import { SecurityRoomView } from "./SecurityRoomView";
import { AdminConsoleView } from "./AdminConsoleView";

export type SecurityCenterTab = "monitor" | "manage";

interface Props {
  /** Which tab to open first. Defaults to "monitor". */
  initialTab?: SecurityCenterTab;
}

const MODE_LABELS: Record<string, string> = {
  off: "Off",
  observe: "Observe",
  ask: "Ask",
  block: "Block",
};

/** Read-only enforcement mode badge shown in the top banner. */
function EnforcementBadge({
  mode,
  configured,
}: {
  mode: string | undefined;
  configured?: boolean;
}) {
  // "(default)" when nobody has chosen a mode. getEnforcement resolves an
  // absent row to the strict default, so the badge otherwise read a flat
  // "Block" for an org that had never configured anything — indistinguishable
  // from a deliberate choice. `configured` was added to the query for exactly
  // this and had no consumer, which made it a capability described and not
  // implemented.
  const label = mode ? (MODE_LABELS[mode] ?? mode) : "…";
  return (
    <span className="security-center__enforce-badge" data-mode={mode ?? "loading"}>
      {label}
      {mode && configured === false ? " (default)" : ""}
    </span>
  );
}

/**
 * SecurityCenter — Wave 1d consolidation.
 *
 * A thin shell that hosts SecurityRoomView ("Monitor") and AdminConsoleView
 * ("Manage") under a single rail destination.  Both children are kept
 * hidden (not unmounted) when their tab is inactive so they preserve their
 * own internal state (selected queue chip, active admin tab, etc.).
 *
 * The parent route (App.tsx → SecurityCenterRoute) lets ANY entitled
 * subscriber (base+) reach this shell, because Manage hosts billing + the Pro
 * GitHub App. Per-tab tier gating lives HERE (Monitor → security) and in
 * AdminConsoleView (per-tab minTier), so non-security users never mount a
 * security-gated pane.
 */
export function SecurityCenter({ initialTab = "monitor" }: Props) {
  // Cumulative-tier gate. Only "security" reaches the Monitor tab
  // (SecurityRoomView's queries are all plan:"security"-gated and would 402
  // for base/pro). hasSecurity defaults to false while the entitlement query
  // is still loading, so non-security users never even momentarily mount the
  // Monitor pane.
  const ent = useEntitlements();
  const hasSecurity = ent.kind === "ready" && ent.hasSecurity;

  // Non-security users have no Monitor tab, so they must default to Manage
  // (the only tab they can use) even when the URL/initialTab says "monitor".
  const resolveTab = (tab: SecurityCenterTab): SecurityCenterTab => (!hasSecurity ? "manage" : tab);

  const [activeTab, setActiveTab] = useState<SecurityCenterTab>(() => resolveTab(initialTab));

  // Fix #1 (Gemini HIGH / Codex P2): SecurityCenter is kept mounted via the
  // App.tsx "visited" set, so useState only initialises once.  When the user
  // navigates to a different tab URL while the component is already mounted
  // (e.g. /security-center?tab=manage) the prop changes but activeTab does
  // not.  Sync explicitly whenever the prop value changes. Also re-runs if the
  // entitlement resolves after first mount, so a non-security user who landed
  // on "monitor" before hasSecurity was known is bounced to "manage".
  useEffect(() => {
    setActiveTab(resolveTab(initialTab));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTab, hasSecurity]);

  // Read the org's current enforcement mode for the banner.
  // policy.queries.getEnforcement returns { mode, posture }.
  const enforcementData = useQuery((convexApi as any).policy.queries.getEnforcement, {});
  const mode = (enforcementData as { mode?: string } | undefined)?.mode;
  const configured = (enforcementData as { configured?: boolean } | undefined)?.configured;

  return (
    <div className="security-center">
      {/* ── Top banner ── */}
      <div className="security-center__banner">
        <div className="security-center__banner-left">
          <span className="security-center__title">Security Center</span>
          <span className="security-center__live-dot" aria-label="Live" title="Live data" />
        </div>
        <div className="security-center__banner-right">
          <span className="security-center__enforce-label">Enforcement:</span>
          <EnforcementBadge mode={mode} configured={configured} />
        </div>
      </div>

      {/* ── Monitor | Manage segmented control ── */}
      <div className="security-center__tabs" role="tablist" aria-label="Security Center tabs">
        {/* Monitor (SecurityRoomView) is security-only. Hide the button entirely
            for base/pro so they never land on a locked pane. */}
        {hasSecurity && (
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "monitor"}
            className={`security-center__tab${activeTab === "monitor" ? " is-active" : ""}`}
            onClick={() => {
              setActiveTab("monitor");
              navigateRoute("/security-center");
            }}
          >
            Monitor
          </button>
        )}
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "manage"}
          className={`security-center__tab${activeTab === "manage" ? " is-active" : ""}`}
          onClick={() => {
            setActiveTab("manage");
            navigateRoute("/security-center?tab=manage");
          }}
        >
          Manage
        </button>
      </div>

      {/* ── Tab panes — kept mounted to preserve child state ── */}
      <div className="security-center__content">
        {/* SecurityRoomView is mounted ONLY for security users. Its queries are
            all plan:"security"-gated, so mounting it for base/pro would fire a
            wall of 402s. Non-security users who somehow reach the Monitor tab
            (defensive) get a compact upgrade prompt instead. */}
        <div hidden={activeTab !== "monitor"} className="security-center__pane">
          {hasSecurity ? (
            <SecurityRoomView />
          ) : (
            <div className="security-center__monitor-locked" role="status">
              <h2>Live monitoring is an Ultra Security feature</h2>
              <p className="muted-fg">
                The Security Room — live policy verdicts, approvals, DLP, and the memory graph —
                requires the Ultra Security plan. Use the Manage tab to upgrade.
              </p>
            </div>
          )}
        </div>
        <div hidden={activeTab !== "manage"} className="security-center__pane">
          <AdminConsoleView />
        </div>
      </div>
    </div>
  );
}
